import { Object3D } from 'three';
import { EngineContext } from './engine-context';

/**
 * Manages hiding/restoring all scene objects when entering/exiting the CAD editor.
 * Extracted from Engine to reduce class size.
 */
export class CADEditorVisibilityManager {
  private hiddenObjects: Map<Object3D, boolean> = new Map();

  constructor(private ctx: EngineContext) {}

  /**
   * Hide all scene objects when entering CAD editor mode.
   * Preserves the CAD editor's own objects (cadGroup).
   */
  hideAll(): void {
    this.hiddenObjects.clear();

    const hide = (obj: Object3D | undefined): void => {
      if (obj && obj.visible) {
        this.hiddenObjects.set(obj, true);
        obj.visible = false;
      }
    };

    hide(this.ctx.pivot);
    hide(this.ctx.optimizedGroup);
    hide(this.ctx.optimizedGroupParametric);
    hide(this.ctx.infoGroup);
    hide(this.ctx.helperGroup);

    // Drill bits
    Object.values(this.ctx.drillBits).forEach((bit) => hide(bit));

    // Symmetry planes
    Object.values(this.ctx.symmetryPlanes).forEach((plane) => hide(plane));

    // Jaws
    hide(this.ctx.jaws?.group);
    hide(this.ctx.jaws?.jawsPivot);
    hide(this.ctx.jaws?.result);

    // Helpers
    hide(this.ctx.arrowHelper);
    hide(this.ctx.normalArrowHelper);
  }

  /**
   * Restore all scene objects that were hidden when entering CAD editor mode.
   */
  restoreAll(): void {
    this.hiddenObjects.forEach((wasVisible, obj) => {
      if (wasVisible) {
        obj.visible = true;
      }
    });
    this.hiddenObjects.clear();
  }
}
